// src/pages/auth/ForgotPasswordPage.tsx
// Request password reset page.

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { observer } from '@react';
import { useAuthnAuth, useI18n } from '@react/hooks';
import { Button, Input, Card, CardHeader, CardTitle, CardDescription, CardContent, CardFooter, Alert, ErrorAlert } from '@react/components';
import { validatePasswordReset } from '../../features/authn/logic';
import { AuthLayout } from './AuthLayout';

export const ForgotPasswordPage = observer(() => {
  const authnAuth = useAuthnAuth();
  const { t } = useI18n();
  const [email, setEmail] = useState('');
  const [errors, setErrors] = useState<Record<string, unknown>>({});
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const validation = validatePasswordReset({ email });
    if (Object.keys(validation).length > 0) {
      setErrors(Object.fromEntries(Object.entries(validation).map(([k, v]) => [k, t(v)])));
      return;
    }

    try {
      await authnAuth.RequestPasswordReset({ email, username: '' });
      setSent(true);
    } catch (err) {
      setErrors({ form: err });
    }
  };

  return (
    <AuthLayout>
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle as="h1" className="text-2xl">{t('auth.forgotPasswordTitle')}</CardTitle>
          <CardDescription>{t('auth.forgotPasswordSubtitle')}</CardDescription>
        </CardHeader>

        {sent ? (
          <CardContent className="space-y-4">
            <Alert variant="success">{t('auth.resetEmailSent')}</Alert>
            <p className="text-sm text-muted-foreground text-center">{t('auth.checkInbox')}</p>
          </CardContent>
        ) : (
          <form onSubmit={handleSubmit}>
            <CardContent className="space-y-4">
              {errors.form ? (
                <ErrorAlert error={errors.form} dismissible onDismiss={() => setErrors((p) => ({ ...p, form: null }))} />
              ) : null}
              <Input
                label={t('auth.email')}
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setErrors({}); }}
                placeholder="you@example.com"
                autoComplete="email"
                error={errors.email as string | undefined}
                required
              />
            </CardContent>
            <CardFooter className="flex-col space-y-4">
              <Button type="submit" fullWidth isLoading={authnAuth.isLoading}>
                {t('auth.sendResetLink')}
              </Button>
              <Link to="/login" className="text-sm text-primary hover:text-primary/80 text-center block">
                {t('auth.backToLogin')}
              </Link>
            </CardFooter>
          </form>
        )}
      </Card>
    </AuthLayout>
  );
});

export default ForgotPasswordPage;
